import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { formatINR, fmtDate, fmtDateTime, GST_LABEL } from "@/lib/format";
import { Pill, Btn, In } from "./kit";

const RecordPayment = ({ quote, busy, run }) => {
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="space-y-2 border-t border-[#27272A] pt-4" data-testid="record-payment-form">
      <In value={method} onChange={setMethod} placeholder="Method (e.g. Bank transfer, UPI)" testid="payment-method" />
      <In value={reference} onChange={setReference} placeholder="Transaction / UTR reference" testid="payment-reference" />
      <In value={note} onChange={setNote} placeholder="Note (optional)" testid="payment-note" />
      <Btn disabled={busy || reference.length < 2} onClick={() => run(() => api.post(`/admin/quotes/${quote.id}/record-payment`, { method, reference, note }), () => "Payment recorded")} data-testid="payment-submit">Confirm payment received</Btn>
    </div>
  );
};

const PaymentInfo = ({ quote, v }) => {
  if (!quote.payment_status) return null;
  const due = Math.round((v.totals.total * (v.deposit_percent ?? 100)) / 100);
  const p = quote.payment;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="quote-payment-info">
      <Pill status={quote.payment_status === "paid" ? "paid" : "awaiting payment"} testid="quote-payment-status" />
      {p ? <span className="text-emerald-300">{formatINR(p.amount_inr)} via {p.method}{p.payment_id || p.reference ? ` · ${p.payment_id || p.reference}` : ""} · {fmtDateTime(p.at)}</span>
        : <span className="text-zinc-400">{formatINR(due)} due ({v.deposit_percent ?? 100}% of total)</span>}
    </div>
  );
};

export const QuoteCard = ({ quote, onRevise, onChange }) => {
  const v = quote.versions[quote.versions.length - 1];
  const [approval, setApproval] = useState(false);
  const [payForm, setPayForm] = useState(false);
  const [method, setMethod] = useState("Email confirmation");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r.data)); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const link = `${window.location.origin}/quote/${quote.token}`;
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5 space-y-4" data-testid={`quote-card-${quote.quote_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="font-mono text-[#00E5FF] text-sm">{quote.quote_number} · v{v.version}</p><p className="mt-1 font-semibold">{v.title}</p></div>
        <Pill status={quote.status} testid="quote-status" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-xs text-zinc-500">Total</p><p className="font-display text-xl font-bold" data-testid="quote-card-total">{formatINR(v.totals.total)}</p><p className="text-xs text-zinc-600">{GST_LABEL[v.gst_mode]}</p></div>
        <div><p className="text-xs text-zinc-500">Valid until</p><p>{fmtDate(v.valid_until)}</p></div>
        <div><p className="text-xs text-zinc-500">Advance</p><p data-testid="quote-card-deposit">{v.deposit_percent ?? 100}%</p></div>
        <div><p className="text-xs text-zinc-500">Revisions</p><p>{v.revision_limit}</p></div>
      </div>
      {quote.accepted && <p className="text-sm text-emerald-300" data-testid="quote-accepted-info">Accepted v{quote.accepted.version} by {quote.accepted.name} · {quote.accepted.method} · {fmtDateTime(quote.accepted.at)}</p>}
      <PaymentInfo quote={quote} v={v} />
      {quote.status !== "draft" && (quote.status !== "accepted" || quote.payment_status === "pending") && (
        <p className="text-xs text-zinc-500 break-all">Client link: <a href={link} target="_blank" rel="noreferrer" className="text-zinc-300 underline" data-testid="quote-client-link">{link}</a></p>
      )}
      <div className="flex flex-wrap gap-2">
        {["draft", "sent"].includes(quote.status) && <Btn disabled={busy} onClick={() => run(() => api.post(`/admin/quotes/${quote.id}/send`), (d) => `Quote ${d.notification.status === "sent" ? "emailed" : "marked sent — email " + d.notification.status}`)} data-testid="quote-send-btn">{quote.status === "sent" ? "Resend" : "Send to client"}</Btn>}
        {quote.status !== "accepted" && <Btn variant="ghost" onClick={onRevise} data-testid="quote-revise-btn">Revise</Btn>}
        {quote.status === "sent" && <Btn variant="ghost" onClick={() => setApproval(!approval)} data-testid="quote-record-approval-toggle">Record approval</Btn>}
        {quote.payment_status === "pending" && <Btn variant="ghost" onClick={() => setPayForm(!payForm)} data-testid="quote-record-payment-toggle">Record offline payment</Btn>}
      </div>
      {payForm && quote.payment_status === "pending" && <RecordPayment quote={quote} busy={busy} run={run} />}
      {approval && (
        <div className="space-y-2 border-t border-[#27272A] pt-4">
          <In value={method} onChange={setMethod} placeholder="Method (e.g. Email, WhatsApp, Call)" testid="approval-method" />
          <In value={note} onChange={setNote} placeholder="Evidence / note (e.g. email received 12 Oct, 4pm)" testid="approval-note" />
          <Btn disabled={busy || note.length < 3} onClick={() => run(() => api.post(`/admin/quotes/${quote.id}/record-approval`, { method, note }), () => "Approval recorded")} data-testid="approval-submit">Confirm recorded approval</Btn>
        </div>
      )}
      {quote.versions.length > 1 && <details className="text-xs text-zinc-500"><summary className="cursor-pointer">Version history</summary>
        {quote.versions.map((x) => <p key={x.version} className="py-1">v{x.version} · {formatINR(x.totals.total)} · {x.created_by} · {fmtDateTime(x.created_at)}</p>)}
      </details>}
    </div>
  );
};
